(() => {
  const WRAPPER_CLASS = 'ohsio-pify-size-chart-wrapper';
  const BUTTON_CLASS = 'ohsio-pify-size-chart-button';
  const LOADER_BUTTON_CLASS = 'ohsio-pify-size-chart-loader';
  const loaderScript = document.currentScript;
  const pifyScriptUrl = loaderScript?.dataset.pifySrc || '';
  const pifyStyleUrl = loaderScript?.dataset.pifyStyle || '';
  const ALLOWED_PRODUCT_HANDLES = new Set([
    'magic-stretch-water-resistant-modular-sofa-cushion-cover',
    'waterproof-stretch-modular-sectional-sofa-cushion-cover',
    'thick-fleece-modular-sofa-cushion-cover',
    'l-shaped-stripe-pet-friendly-sofa-cushion-cover',
    'ohsio-stretch-pet-friendly-sectional-couch-cushion-covers',
    'ohsio-thick-fleece-couch-cushion-covers',
    'ohsio-stretch-fleece-sectional-couch-cushion-covers',
  ]);
  let scheduled = false;
  let observedRoot = null;
  let observer = null;
  let pifyAssetsPromise;

  const normalizedUrl = (value) => {
    try {
      return new URL(value, window.location.href).href;
    } catch {
      return value || '';
    }
  };

  const findAsset = (selector, attribute, url) =>
    Array.from(document.querySelectorAll(selector)).find(
      (element) => normalizedUrl(element.getAttribute(attribute)) === normalizedUrl(url)
    );

  const loadPifyStyle = () => {
    if (!pifyStyleUrl) return Promise.resolve();

    const existing = findAsset('link[rel~="stylesheet"][href]', 'href', pifyStyleUrl);
    if (existing) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = pifyStyleUrl;
      link.dataset.ohsioDeferredPify = 'true';
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', () => reject(new Error('Unable to load the Pify stylesheet.')), {
        once: true,
      });
      document.head.appendChild(link);
    });
  };

  const loadPifyScript = () => {
    if (!pifyScriptUrl) return Promise.reject(new Error('Missing the Pify script URL.'));

    const existing = findAsset('script[src]', 'src', pifyScriptUrl);
    if (existing) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = pifyScriptUrl;
      script.async = true;
      script.dataset.ohsioDeferredPify = 'true';
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', () => reject(new Error('Unable to load the Pify size chart.')), {
        once: true,
      });
      document.head.appendChild(script);
    });
  };

  const loadPifyAssets = () => {
    pifyAssetsPromise ||= Promise.all([loadPifyStyle(), loadPifyScript()]).catch((error) => {
      pifyAssetsPromise = undefined;
      throw error;
    });
    return pifyAssetsPromise;
  };

  const pifyButton = (wrapper) =>
    wrapper.querySelector('.pify_inline_button, .pify_size_chart_button');

  const waitForPifyButton = (wrapper) => {
    const existing = pifyButton(wrapper);
    if (existing) return Promise.resolve(existing);

    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        buttonObserver.disconnect();
        reject(new Error('The Pify size chart button was not created in time.'));
      }, 10000);
      const buttonObserver = new MutationObserver(() => {
        const button = pifyButton(wrapper);
        if (!button) return;
        window.clearTimeout(timeout);
        buttonObserver.disconnect();
        resolve(button);
      });
      buttonObserver.observe(wrapper, { childList: true, subtree: true });
    });
  };

  const ownText = (element) =>
    Array.from(element.childNodes)
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .map((node) => node.textContent || '')
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

  const isSizeHeading = (element) => {
    const text = ownText(element) || element.textContent || '';
    return /(^|\s)size(\s|:|$)/i.test(text.trim());
  };

  const findSizeHeading = (picker) => {
    const candidates = picker.querySelectorAll(
      '.variant-option > legend, .variant-option--dropdowns > label, [data-testid="variant-option-single"]'
    );

    return Array.from(candidates).find(isSizeHeading) || null;
  };

  const displayableChart = () => {
    if (!Array.isArray(window.sizeChartData)) return null;
    return window.sizeChartData.find(Boolean) || null;
  };

  const makeFallbackButton = () => {
    const chart = displayableChart();
    if (!chart || !window.SizeChart?.createModal) return null;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = `pify_inline_button pify-overlay-button ${BUTTON_CLASS}`;
    button.textContent = window.settings?.buttonText || 'Size Chart';
    button.setAttribute('aria-haspopup', 'dialog');
    button.addEventListener('click', () => {
      const modal = window.SizeChart.createModal(chart);
      if (modal) document.body.appendChild(modal);
    });
    return button;
  };

  const makeLoaderButton = (wrapper) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `${BUTTON_CLASS} ${LOADER_BUTTON_CLASS}`;
    button.textContent = 'Size Chart';
    button.setAttribute('aria-haspopup', 'dialog');

    const prime = () => loadPifyAssets().catch(() => {});
    button.addEventListener('pointerenter', prime);
    button.addEventListener('focus', prime);
    button.addEventListener('pointerdown', prime);
    button.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') prime();
    });
    button.addEventListener('click', async () => {
      if (button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');

      try {
        await loadPifyAssets();
        const nativeButton = pifyButton(wrapper) || Array.from(document.querySelectorAll('.pify_size_chart_wrapper'))
          .map(pifyButton).find(Boolean);
        if (nativeButton) {
          nativeButton.click();
        } else {
          const fallback = makeFallbackButton();
          if (!fallback) throw new Error('The product size chart is not available.');
          fallback.click();
        }
      } catch (error) {
        button.removeAttribute('aria-busy');
        console.error('Unable to open the size chart.', error);
      }
    });
    return button;
  };

  const positionButton = () => {
    const customPicker = document.querySelector('[data-ohsio-nolan-picker]');
    const customSlot = customPicker?.querySelector('[data-size-chart-slot]');
    if (customPicker && customSlot) {
      let wrapper = customSlot.querySelector(`.${WRAPPER_CLASS}`);
      const existing = Array.from(document.querySelectorAll('.pify_size_chart_wrapper')).find((candidate) =>
        candidate !== wrapper && candidate.querySelector('.pify_inline_button, .pify_size_chart_button')
      );
      if (existing) {
        wrapper?.remove();
        wrapper = existing;
        wrapper.classList.add('pify_size_chart_wrapper', WRAPPER_CLASS);
        customSlot.appendChild(wrapper);
      } else if (!wrapper) {
        wrapper = document.createElement('span');
        wrapper.classList.add('pify_size_chart_wrapper', WRAPPER_CLASS);
        customSlot.appendChild(wrapper);
      }

      const nativeButton = pifyButton(wrapper);
      if (nativeButton) {
        wrapper.querySelectorAll(`.${LOADER_BUTTON_CLASS}`).forEach((button) => button.remove());
      } else {
        const fallback = makeFallbackButton();
        if (fallback) {
          wrapper.querySelectorAll(`.${LOADER_BUTTON_CLASS}`).forEach((button) => button.remove());
          wrapper.appendChild(fallback);
        } else if (!wrapper.querySelector(`.${LOADER_BUTTON_CLASS}`)) {
          wrapper.appendChild(makeLoaderButton(wrapper));
        }
      }
      return;
    }

    const picker =
      document.querySelector('variant-picker[data-template-product-match="true"]') ||
      document.querySelector('main variant-picker');
    if (!picker) return;

    const heading = findSizeHeading(picker);
    if (!heading) return;

    heading.classList.add('size_chart_title', 'ohsio-size-chart-heading');

    let wrapper = heading.querySelector(`.${WRAPPER_CLASS}`);
    if (!wrapper) {
      const nativeWrapper = Array.from(
        document.querySelectorAll('.pify_size_chart_wrapper')
      ).find((candidate) =>
        candidate.querySelector('.pify_inline_button, .pify_size_chart_button')
      );

      wrapper = nativeWrapper || document.createElement('span');
      wrapper.classList.add('pify_size_chart_wrapper', WRAPPER_CLASS);
      heading.appendChild(wrapper);
    }

    const nativeButton = pifyButton(wrapper);
    if (nativeButton) {
      wrapper.querySelectorAll(`.${LOADER_BUTTON_CLASS}`).forEach((button) => button.remove());
    } else {
      const fallback = makeFallbackButton();
      if (fallback) {
        wrapper.querySelectorAll(`.${LOADER_BUTTON_CLASS}`).forEach((button) => button.remove());
        wrapper.appendChild(fallback);
      } else if (!wrapper.querySelector(`.${LOADER_BUTTON_CLASS}`)) {
        wrapper.appendChild(makeLoaderButton(wrapper));
      }
    }
  };

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      positionButton();
      observeProductInformation();
    });
  };

  const observeProductInformation = () => {
    const root = document.querySelector('main .product-information');
    if (!root || root === observedRoot) return;

    observer?.disconnect();
    observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true });
    observedRoot = root;
  };

  const start = () => {
    const productHandle = window.location.pathname.match(/^\/products\/([^/?#]+)/)?.[1] || '';
    if (!ALLOWED_PRODUCT_HANDLES.has(productHandle)) {
      document.querySelectorAll(`.${WRAPPER_CLASS}`).forEach((wrapper) => wrapper.remove());
      return;
    }

    schedule();
    observeProductInformation();

    [500, 1500, 4000, 12500].forEach((delay) => window.setTimeout(schedule, delay));
    document.addEventListener('shopify:section:load', () => {
      observedRoot = null;
      observeProductInformation();
      schedule();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
